import { quoteRequestApi } from 'apiConnector/quote/quote';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics/tabRemoved';
import type { QuoteRequestPayload } from 'models/quote/QuoteRequestPayload';
import { ofType } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { selectEssentialFormInfos } from 'state/essential/infos/selectors';
import { essentialQuoteAckedThunk, essentialQuoteRequestFailedThunk } from 'state/essential/thunks';
import { QuoteRequested } from 'state/forms/actions/quote';
import { getFormEnrichedData, getUserDefaultProductView } from 'state/selectors';
import type { EpicWithThunk } from 'state/state.common';

export function essentialRequestQuoteEpic(): EpicWithThunk {
  return (action$, state$, { frontLogger, httpClient, getSgmeConfiguration }) =>
    action$.pipe(
      ofType('QUOTE_REQUESTED'),
      filter(action => action.workspace === 'essential'),
      withLatestFrom(state$),
      mergeMap(([{ tabId, quoteType, quoteRequestStartingTimeInMillis, isReprice, ...action }, state]) => {
        const { quoteQuery, negotiationId, productDescription, version, sendWholeFormData, instrumentName } =
          selectEssentialFormInfos(state, tabId)!;
        const productView = getUserDefaultProductView(state);
        const rfq = sendWholeFormData ? getFormEnrichedData(state, tabId) : quoteQuery;
        const frontReconciliationId = (action as QuoteRequested).frontReconciliationId;
        const request: QuoteRequestPayload = {
          quoteQuery: rfq,
          negotiationId,
          productSubType: productDescription,
          instrumentContractName: productDescription,
          version,
          instrumentName,
          productView,
          tabId,
          reconciliationId: frontReconciliationId,
          isReprice,
        };
        const quoteAckTimeoutMs = getSgmeConfiguration().quoteAckTimeoutMs;

        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        return quoteRequestApi(httpClient, quoteAckTimeoutMs)
          .requestQuote(request, 'essential', quoteType)
          .pipe(
            takeUntil(tabRemoved$),
            map(
              ({
                quoteId,
                quoteExternalId,
                pricingEstimatedDurationInMilliseconds,
                newVersion,
                isExecOnlyAddon,
                details,
                sidePanelDetails,
              }) => {
                return essentialQuoteAckedThunk(
                  tabId,
                  quoteId,
                  quoteType,
                  quoteExternalId,
                  negotiationId,
                  pricingEstimatedDurationInMilliseconds,
                  quoteRequestStartingTimeInMillis,
                  newVersion,
                  isExecOnlyAddon,
                  details,
                  frontReconciliationId,
                  sidePanelDetails,
                );
              },
            ),
            catchError((error: Error) => {
              frontLogger.error('An error occurred during essential quote request {@FrontError}', error);
              const quoteRequestFailed = essentialQuoteRequestFailedThunk(
                tabId,
                apiErrorHelper.toApiErrorMessage(error, { timeoutErrorMessage: 'quote.errorMessages.timeout' }),
                frontReconciliationId,
              );

              return of(quoteRequestFailed);
            }),
          );
      }),
    );
}
